import { memo, useRef, useState, useCallback, useEffect, useLayoutEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../../store/storeTypes';
import ItemMenu from '../components/ItemMenu';
import MenuSubHeader from '../components/MenuSubHeader';
import Layout from '../../../components/Layout';
import { historyMW } from '../../../helpers/routingHelpers';
import useWindowDimensions from '../../../hooks/useWindowDimensions';
import { valueWithAppliedRewards } from '../../../helpers/basketHelpers';
import BasketIcon from '../../../img/icons/Basket.svg';
import chevronLeftWhite from '../../../img/icons/ChevronLeftWhite.svg';
import PopupBannerController from '../../../components/PopupBannerController';
import { useMostVisibleElement } from '../../../hooks/useMostVisibleElement';
import { useConfig } from '../../../helpers/useConfig';

function isBrowserSimulation() {
  const match = window.matchMedia || window.msMatchMedia;
  if (match) {
    const mq = match("(pointer:coarse)");
    return !mq.matches;
  }
  return true;
}

const Category = memo(({
  refs,
  addItem,
  removeItem,
  navigateTo,
  refreshBasket,
  inApp,
  makeItAMealBundles,
  makeItAMealBundleOptions,
  makeItAMealVariant,
  makeItAMealModalTimesShown,
  setMakeItAMealModalTimesShown,
  showMenuHeaderAtTopOfPage
}) => {
  const { categoryName } = useParams();
  const decodedCategoryName = decodeURIComponent(categoryName || '');
  const navigate = useNavigate();
  const { width } = useWindowDimensions();
  const config = useConfig();
  const basket = useAppSelector(state => state.basket);
  const categories = useAppSelector(state => state.menu.categories);
  const restaurant = useAppSelector(state => state.session.restaurant);
  const freshRelevanceInitialised = useAppSelector(state => state.session.freshRelevanceInitialised);
  const showCalories = useAppSelector(state => state.menu.showCalories);
  const isTabletScreen = useAppSelector(state => state.session.isTabletScreen);
  const isBackButtonOverlapping = width < 1500;

  const categoryIndex = categories?.findIndex(
    (c) => c.name.toLowerCase() === decodedCategoryName.toLowerCase()
  );
  const category = categoryIndex >= 0 ? categories[categoryIndex] : null;

  const categoryRefs = useRef([]);
  const subCategoryHeaderRefs = useRef([]);
  const subCategoryRefs = useRef([]);
  const [selectedSubCategory, setSelectedSubCategory] = useState(0);
  const [showBackButton, setShowBackButton] = useState(false);
  const [quickAddModalState, setQuickAddModalState] = useState(false);
  const [makeItAMealModalState, setMakeItAMealModalState] = useState(false);

  const handleMakeItAMealModalState = useCallback((state) => {
    setMakeItAMealModalState(state);
    setMakeItAMealModalTimesShown(prev => prev + 1);
  }, [makeItAMealModalTimesShown]);

  const handleBack = () => {
    navigate(-1);
  };

  // Hide the existing BackMenuButton when on category page
  useLayoutEffect(() => {
    if (refs.current.backButtonRef) {
      refs.current.backButtonRef.style.display = 'none';
    }
    return () => {
      if (refs.current.backButtonRef) {
        refs.current.backButtonRef.style.display = '';
      }
    };
  }, [refs]);

  // Show back chevron in sticky header when Hero's back button scrolls out of view
  useEffect(() => {
    const onScroll = () => {
      const navHeight = refs.current.navRef?.clientHeight ?? 100;
      const isHeroBackButtonVisible = refs.current.heroBackButtonRef
        ? refs.current.heroBackButtonRef.getBoundingClientRect().bottom - navHeight > 0
        : false;
      setShowBackButton(!isHeroBackButtonVisible);
    };
    window.addEventListener('scroll', onScroll, true);
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [refs]);

  // Track most visible subcategory for the sub-header highlight
  useMostVisibleElement(
    subCategoryRefs.current[0],
    setSelectedSubCategory,
    subCategoryRefs.current.every((ref) => ref !== null),
    refs,
    isBackButtonOverlapping,
    inApp,
    showMenuHeaderAtTopOfPage
  );

  const scrollTo = (titleRef) => {
    const isSimulation = isBrowserSimulation();
    const topPadding = 20;
    const navHeight = refs.current.navRef?.clientHeight ?? 100;
    const headerHeight = refs.current.headerRef?.clientHeight ?? 0;
    const categoryHeaderHeight = 50;
    const verticalOffset = navHeight + headerHeight + categoryHeaderHeight + topPadding;

    const currentScrollY = isSimulation
      ? document.documentElement.scrollTop
      : window.scrollY;

    const newScroll = currentScrollY + titleRef.getBoundingClientRect().top - verticalOffset;
    window?.scrollTo({ top: newScroll });
  };

  const scrollToSubCategory = (index) => {
    if (index === 0) {
      const firstHeader = subCategoryHeaderRefs.current[0]?.[0];
      if (firstHeader) {
        scrollTo(firstHeader);
      }
      setSelectedSubCategory(0);
    } else {
      const subCategoryTitle = subCategoryHeaderRefs.current[0]?.[index + 1];
      if (!subCategoryTitle) return;
      scrollTo(subCategoryTitle);
      setSelectedSubCategory(index);
    }
  };

  // If category not found, redirect back to menu
  if (!category) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Category not found.</p>
        <button className="btn btn-primary" onClick={handleBack}>
          Back to menu
        </button>
      </div>
    );
  }

  const singleCategoryArray = [category];
  const subCategories = category.subCategories
    ? [...category.subCategories].sort((a, b) => a.displayIndex - b.displayIndex)
    : [];

  const findItemQuantity = (id, isBundle = false, itemName = null) => {
    const items = isBundle
      ? basket.bundles.filter(el => el.name === id)
      : basket.items.filter(el => el.id === id && (!itemName || el.name === itemName));
    return items.reduce((acc, cur) => acc += cur.quantity, 0);
  };

  return (
    <div>
      <Layout
        refs={refs}
        backgroundColour='#3AAA20'
        ctaCopy={basket.isDelivery ? '' : 'Change restaurant'}
        subtitle={
          basket.isDelivery
            ? `Estimated delivery time: ${basket?.delivery?.deliveryQuote ? `${basket.delivery.deliveryQuote.duration?.begin} - ${basket.delivery.deliveryQuote.duration?.end} mins` : ''}`
            : `You are ordering for collection${restaurant && restaurant.name ? ' from ' + restaurant.name + ' restaurant.' : '.'}`
        }
        backButton={handleBack}
        extraVerticalPadding
        allergens={true}
        isFluid={true}
        heroPaddingSize='is-xxsmall'
        isSmallerTitle
        customStyles={'is-menu'}
      >
        <div
          className="category-page-sticky-headers"
          style={{ zIndex: 300, position: 'sticky', top: (refs.current.navRef?.clientHeight ?? 0) }}
        >
          <div className="category-page-header">
            {showBackButton && (
              <button
                className="back-btn"
                onClick={handleBack}
                aria-label="Back to menu"
                data-testid="category-back-button"
                style={{ position: 'relative', left: 0, top: 0 }}
              >
                <img src={chevronLeftWhite} alt="Back" style={{ width: 24, height: 24 }} />
                <span className="pl-2 back-btn-text" style={{ color: 'white' }}>Back</span>
              </button>
            )}
            <h2 className="category-page-header__title">{category.name}</h2>
          </div>

          {subCategories.length > 1 && (
            <MenuSubHeader
              subCategories={subCategories}
              selectedCategory={0}
              selectedSubCategory={selectedSubCategory}
              inApp={inApp}
              scrollToSubCategory={scrollToSubCategory}
              isTabletScreen={isTabletScreen}
              refs={refs}
            />
          )}
        </div>

        <div ref={r => refs.current.menuContainerRef = r} className='container' style={{ paddingBottom: '1rem', zIndex: 250 }}>
          <div className="menu menu-wrapper">
            <div className='is-flex-wrap-wrap is-align-content-stretch' style={{ width: '100%' }}>
              <ItemMenu
                categoryRefs={categoryRefs}
                subCategoryHeaderRefs={subCategoryHeaderRefs}
                subCategoryRefs={subCategoryRefs}
                menuCategories={singleCategoryArray}
                basket={basket}
                navigateTo={navigateTo}
                addItem={addItem}
                removeItem={removeItem}
                findItemQuantity={findItemQuantity}
                setQuickAddModalState={setQuickAddModalState}
                quickAddModalState={quickAddModalState}
                setMakeItAMealModalState={handleMakeItAMealModalState}
                makeItAMealModalState={makeItAMealModalState}
                makeItAMealBundles={makeItAMealBundles}
                makeItAMealBundleOptions={makeItAMealBundleOptions}
                makeItAMealVariant={makeItAMealVariant}
                makeItAMealModalTimesShown={makeItAMealModalTimesShown}
                refs={refs}
                showMenuHeaderAtTopOfPage={showMenuHeaderAtTopOfPage}
                inApp={inApp}
              />
            </div>
            {showCalories && (
              <div className={`nutrition-note ${inApp && basket?.numberOfItems ? 'in-app-with-basket' : ''}`}>
                <span>Adults need around 2000 kcal per day.</span>
              </div>
            )}
          </div>
        </div>
        <div
          ref={r => refs.current.footerSpacer = r}
          style={{
            height: inApp ? (refs.current.bannerRef?.clientHeight ?? 0) + (refs.current.headerRef?.clientHeight ?? 0) : 150,
            width: '100%'
          }}
        />
        {basket.numberOfItems ? (
          <PopupBannerController refs={refs} basket={basket} screenWidth={width} showMenuHeaderAtTopOfPage={showMenuHeaderAtTopOfPage} hide isMenu justifyEnd>
            <button
              onClick={() => {
                if (!freshRelevanceInitialised) { /* upsell logic if needed */ }
              }}
              className={'tobasket-btn btn btn-primary w100-mobile'}
              data-testid={'tobasket'}
            >
              <img alt='Basket' src={BasketIcon} />
              {`Checkout - ${config?.CURRENCY_SYMBOL}${valueWithAppliedRewards(basket?.subTotal - basket?.voucherReduction, basket?.discounts).toFixed(2)}`}
            </button>
          </PopupBannerController>
        ) : null}
      </Layout>
    </div>
  );
});

Category.displayName = 'Category';
export default Category;
